import { ContentListSort } from '@kindo/api';
import {
  insertIf,
  INTEGRATION_DISPLAY_NAMES,
  isAudioFileType,
  isIndexableVectorSearchFileType,
  isIntegration,
  isVideoFileType
} from '@kindo/universal';
import { formatDistanceToNowStrict } from 'date-fns';

import { ProgressBar } from '../../ProgressBar';

import { FileResource } from './AddFileModal.types';
import { FileUploadStatus } from './FileUploader';

import {
  Icon,
  IconButton,
  IconButtonType,
  TableColumn,
  TableRow,
  TextIconColor,
  Typography,
  TypographyFont,
  TypographySize,
  TypographyWeight
} from '~/components/core';
import { withTooltip } from '~/components/core/hocs';
import { ContentIcon } from '~/components/shared';
import { getContentFetchUrl } from '~/constants';

export type FileResourceWithTableMetadata = FileResource & {
  hasPlaintextTranscription: boolean;
  isSupportedByModel: boolean;
  isTooLargeToDownloadFromMerge: boolean;
  isUploadedToCorrespondingCloud: boolean;
  llamaIndexedAt: string | null;
};

export const shouldShowFileTranscriptionDownload = (
  file: FileResourceWithTableMetadata
): {
  isTranscriptionReady: boolean;
  shouldShowTranscriptionDownload: boolean;
} => {
  const shouldShowTranscriptionDownload =
    !!file.fileType &&
    (isVideoFileType(file.fileType) || isAudioFileType(file.fileType));

  return {
    shouldShowTranscriptionDownload,
    isTranscriptionReady: file.hasPlaintextTranscription
  };
};

const ellipsis = (text: string, max = 35) => {
  if (text.length > max) {
    return (
      text.substring(0, 20) +
      '...' +
      text.substring(text.length - 10, text.length)
    );
  }
  return text;
};

export const getFileTableColumns = (
  hasAnyTranscriptions: boolean
): TableColumn<ContentListSort>[] => [
  {
    title: 'NAME',
    width: 'w-1/12',
    sortKey: ContentListSort.FILE_NAME
  },
  {
    title: 'TYPE',
    width: 'w-1/12',
    sortKey: ContentListSort.FILE_TYPE
  },
  {
    title: 'SOURCE',
    width: 'w-[12.5%]',
    sortKey: ContentListSort.INTEGRATION
  },
  {
    title: 'UPLOADED',
    width: 'w-[12.5%]',
    sortKey: ContentListSort.CREATED_AT
  },
  ...insertIf<TableColumn<ContentListSort>>(hasAnyTranscriptions, {
    title: '', // Download transcription
    width: 'w-1/12'
  }),
  {
    title: '', // Remove file
    width: 'w-1/12'
  }
];

// Custom component for file upload row that spans the entire table
export const FileUploadRow = ({ upload }: { upload: FileUploadStatus }) => {
  const tooltipContent = `Uploading ${upload.file.name} ${upload.progress}%`;

  const rowContent = (
    <tr className="h-14" data-testid="file-upload-row">
      {/* First cell for selection checkbox column */}
      <td />
      {/* Second cell for file name */}
      <td className="font-ibmPlexMono py-2 text-sm text-white">
        <span>{ellipsis(upload.file.name)}</span>
      </td>
      {/* Third cell for progress bar that spans remaining columns */}
      <td colSpan={100}>
        <ProgressBar value={upload.progress} />
      </td>
    </tr>
  );

  return withTooltip(rowContent, { content: tooltipContent });
};

export const getFileUploadRow = (
  upload: FileUploadStatus
): React.ReactElement => (
  <FileUploadRow key={upload.file.name} upload={upload} />
);

// Determine the tooltip message and text color based on file status
const getFileTooltipAndColor = (
  file: FileResourceWithTableMetadata,
  isKnowledgeStore: boolean
) => {
  // If the file is both "not supported by the model" and "not ready for use",
  // prioritize the "not supported by the model" status for visual indication
  if (!file.isSupportedByModel) {
    return {
      disabled: true,
      toolTip: 'File type not supported by the model',
      disabledTextColor: TextIconColor.SECONDARY
    };
  }
  if (!file.isUploadedToCorrespondingCloud) {
    if (file.isTooLargeToDownloadFromMerge) {
      return {
        disabled: true,
        toolTip: 'File is too large to download',
        disabledTextColor: TextIconColor.DISABLED
      };
    }
    return {
      disabled: true,
      toolTip: 'File not yet ready for use',
      disabledTextColor: TextIconColor.DISABLED
    };
  }
  if (
    isKnowledgeStore &&
    file.fileType &&
    !isIndexableVectorSearchFileType(file.fileType)
  ) {
    return {
      disabled: true,
      toolTip: 'File type cannot be used in Knowledge Store',
      disabledTextColor: TextIconColor.DISABLED
    };
  }
  if (file.llamaIndexedAt === null && isKnowledgeStore) {
    return {
      disabled: true,
      toolTip: 'File has not been processed yet',
      disabledTextColor: TextIconColor.DISABLED
    };
  }
  return {
    disabled: false
  };
};

export const getFileRow = (
  file: FileResourceWithTableMetadata,
  onRemoveFile: (fileId: string) => void,
  hasAnyTranscriptions: boolean,
  isSelected: boolean,
  isKnowledgeStore: boolean
): TableRow => {
  const { shouldShowTranscriptionDownload, isTranscriptionReady } =
    shouldShowFileTranscriptionDownload(file);

  const { toolTip, disabledTextColor, disabled } = getFileTooltipAndColor(
    file,
    isKnowledgeStore
  );

  const source = isIntegration(file.source)
    ? INTEGRATION_DISPLAY_NAMES[file.source]
    : file.source;

  // Handle transcription download button
  const transcriptionButton = () => {
    if (!hasAnyTranscriptions) return [];
    if (!shouldShowTranscriptionDownload) return [undefined];

    return [
      <IconButton
        disabled={!isTranscriptionReady}
        href={getContentFetchUrl({
          contentId: file.id,
          isDownload: true,
          isPlaintext: true
        })}
        icon={Icon.EDIT_FILE}
        key={`download-transcription-${file.id}`}
        title="Download Transcription"
        type={IconButtonType.OUTLINED}
      />
    ];
  };

  return {
    disabled,
    disabledTextColor,
    toolTip,
    key: file.id,
    height: 'h-14',
    cells: [
      <Typography
        color={isSelected ? TextIconColor.HIGHLIGHT : TextIconColor.PRIMARY}
        font={TypographyFont.HEADING}
        key={`${file.id}-name`}
        size={TypographySize.SMALL}
        weight={TypographyWeight.NORMAL}
      >
        {file.name}
      </Typography>,
      file.fileType,
      // Source
      <div
        className="flex items-center gap-1"
        data-testid="file-source-container"
        key={file.id}
      >
        <ContentIcon contentSource={file.source} />
        <Typography
          color={disabledTextColor}
          font={TypographyFont.HEADING}
          size={TypographySize.SMALL}
          weight={TypographyWeight.NORMAL}
        >
          {source}
        </Typography>
      </div>,
      // Uploaded (createdAt)
      <Typography
        key={`uploaded-${file.id}`}
        size={TypographySize.SMALL}
        weight={TypographyWeight.NORMAL}
      >
        {formatDistanceToNowStrict(new Date(file.createdAt), {
          addSuffix: true
        })}
      </Typography>,
      ...transcriptionButton(),
      <IconButton
        icon={Icon.TRASH}
        key={`remove-${file.id}`}
        onClick={() => onRemoveFile(file.id)}
        title="Remove File"
        type={IconButtonType.NONE}
      />
    ]
  };
};
